import {SimulationRequestJson} from "../../../business/ApiJsonTypes";
import {isSuccessful, useImpellerDatasheet} from "../../../business/useImpellerDatasheet";
import UboldSpinner from "../../../ubold-components/UboldSpinner";
import {useEffect, useState} from "react";
import {OptiselModal} from "../../../components/modal/OptiselModal";
import {AssemblyScoreBar} from "../../../components/assembly/AssemblyScoreBar";
import {createInternationalizationPrefixer} from "../../../platform/i18n/i18n";
import {useHasCurrentUserPrivilege} from "../../../layout/Protected";

const t = createInternationalizationPrefixer("MechanicalProbe");
const noop = (lastUsedConfigurationDiscovered: any) => {};

const BadgeNotAvailable = () => <div className={"badge badge-sm badge-danger"}>N/A</div>;

const BadgeOverload = (props: {onClick?: () => void}) => (
    <div
        className={"badge badge-sm badge-warning"}
        style={{cursor: props.onClick ? "pointer" : "default"}}
        onClick={props.onClick}
    >
        Ov.!
    </div>
);

const BadgeBpfResonating = (props: {onClick?: () => void}) => (
    <div
        className={"badge badge-sm badge-warning"}
        style={{cursor: props.onClick ? "pointer" : "default"}}
        onClick={props.onClick}
    >
        BPF
    </div>
);

const BadgeSuccess = (props: {onClick?: () => void}) => (
    <div
        className={"badge badge-sm badge-success"}
        style={{cursor: props.onClick ? "pointer" : "default"}}
        onClick={props.onClick}
    >
        OK
    </div>
);

export function MechanicalProbe(props: {
    simulationRequest: SimulationRequestJson,
    matchId: string,
    onProbeCompleted: () => any
}) {
    const [modalOpened, setModalOpened] = useState(false);
    const canSeeTriageDetails = useHasCurrentUserPrivilege("PERM_SELECTION_TRIAGE_DETAILED_PARAMETERS") === true;

    const {datasheetResponse, loading} = useImpellerDatasheet(
        {...props.simulationRequest, probingOnly: true},
        noop,
        "matchid-" + props.matchId
    );

    useEffect(() => {
        if (datasheetResponse !== undefined) {
            props.onProbeCompleted();
        }
    }, [datasheetResponse]);

    if (loading) {
        return <div><UboldSpinner/></div>;
    }

    if (!isSuccessful(datasheetResponse) ||
        datasheetResponse.result.mechanicalResultDatasheetParameters === null) {
        return <div><BadgeNotAvailable/></div>;
    }

    const mechanicalParams = datasheetResponse.result.mechanicalResultDatasheetParameters;
    const mechanicalScore = mechanicalParams.mechanicalSelectionScore;
    const bpfResonating = mechanicalParams.resonanceReport.bpfResonating;

    const modalContent = (
        <OptiselModal
            header={t("Wynik triażu mechanicznego")}
            opened={modalOpened}
            closeable={true}
            onClose={() => setModalOpened(false)}
        >
            <div className="mb-3">
                {mechanicalScore ? (
                    <AssemblyScoreBar mechanicalSelectionScore={mechanicalScore} asColumn={true}/>
                ) : (
                    <div className="text-center text-muted p-3">
                        {t("Szczegółowe wyniki nie są dostępne dla tej konfiguracji")}
                        <div className="mt-2">
                            {mechanicalParams.possibleOverload ?
                                t("Status: Przeciążenie") :
                                t("Status: OK")}
                        </div>
                    </div>
                )}
            </div>
            <div className="text-center mt-3">
                <button
                    className="btn btn-primary"
                    onClick={() => setModalOpened(false)}
                >
                    {t("Zamknij")}
                </button>
            </div>
        </OptiselModal>
    );

    const openModal = canSeeTriageDetails ? () => setModalOpened(true) : undefined;
    const optionalBadgeResonating = bpfResonating ?
        <BadgeBpfResonating onClick={openModal}/> : null;

    if (mechanicalParams.possibleOverload) {
        return (
            <>
                <div>
                    <BadgeOverload onClick={openModal}/>
                    {optionalBadgeResonating}
                </div>
                {canSeeTriageDetails && modalContent}
            </>
        );
    }

    return (
        <>
            <div>
                <BadgeSuccess onClick={openModal}/>
                {optionalBadgeResonating}
            </div>
            {canSeeTriageDetails && modalContent}
        </>
    );
}

export function MechanicalProbeExplainer() {
    const BadgeNotAvailable = () => <div className={"badge badge-sm badge-danger"}>N/A</div>;
    const BadgeOverload = () => <div className={"badge badge-sm badge-warning"}>Ov.!</div>;
    const BadgeSuccess = () => <div className={"badge badge-sm badge-success"}>OK</div>;

    return <p>
        <strong>{t("Triaż mechaniczny")}:</strong> <BadgeNotAvailable/> - {t("brak rozwiązania")}, <BadgeOverload/> - {t("Przeciążenie")}, <BadgeSuccess/> - {t("Udane nieprzeciążone złożenie")}
    </p>;
}
